import { useCallback } from 'react';
import type { ApolloError } from 'apollo-client';
import { AbortError } from '@atlassian/jira-business-error-handling/src/utils/abort-error/index.tsx';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
	useExperienceAbort,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import type { ChildrenQueryData } from '@atlassian/jira-business-list-handlers/src/controllers/children/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	type default as FetchError,
	type ValidationError,
	isValidationError,
} from '@atlassian/jira-fetch/src/utils/errors.tsx';
import {
	isClientFetchError,
	isHttpClientErrorResponse,
} from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { useFlagService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import { JIRA_SCHEDULE_ITEM_EXPERIENCE } from '../../../common/constants.tsx';
import type {
	TimelineViewIssuesReturn,
	TimelineViewIssue,
	UpdateItemsInCache,
	UpdateIssueField,
	ScheduleIssueField,
} from '../../../services/get-timeline-view-issues/types.tsx';
import { useScheduleItem } from '../../../services/jira-schedule-items/index.tsx';
import { useTimelineUI } from '../../timeline-ui/index.tsx';
import { useLockIssue } from '../../timeline-updater/index.tsx';
import {
	START_DATE_MISSING_FLAG,
	SCHEDULE_ITEM_FAIL_FLAG,
	SCHEDULE_ITEM_NOT_FOUND,
	SCHEDULE_ITEM_NOT_FOUND_ISSUE_TERM_REFRESH,
} from '../constants.tsx';
import type { OnJiraItemUpdate } from '../types.tsx';
import { toISOString } from '../utils.tsx';
import { shouldScheduleChildrenIssues, getIssuesBulkDatesUpdate } from './utils.tsx';

type Props = {
	issuesMap: Map<string, TimelineViewIssue>;
	updateItemsInCache: UpdateItemsInCache;
	childrenQueryData: ChildrenQueryData<TimelineViewIssuesReturn>;
};

type UpdateItemsInCacheList = {
	bulkUpdates: (UpdateIssueField | ScheduleIssueField)[];
	originalValues: UpdateIssueField[];
}[];

export const useUpdateItem = ({ issuesMap, updateItemsInCache, childrenQueryData }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagService();
	const lockIssue = useLockIssue();
	const [{ timelineDuration }] = useTimelineUI();
	const { onScheduleItem } = useScheduleItem();
	const { data: startDateField } = useStartDateField();

	const startExperience = useExperienceStart(JIRA_SCHEDULE_ITEM_EXPERIENCE);
	const markExperienceAborted = useExperienceAbort(JIRA_SCHEDULE_ITEM_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(JIRA_SCHEDULE_ITEM_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(JIRA_SCHEDULE_ITEM_EXPERIENCE);

	const onItemUpdate: OnJiraItemUpdate = useCallback(
		(id, param) => {
			const issue = issuesMap.get(id);
			if (!issue || !param.dates) {
				return;
			}
			const { dates } = param;

			if (!dates?.startDate && !dates?.dueDate) {
				// no dates? no problem: do nothing!
				return;
			}

			const startDate = dates?.startDate?.date != null ? toISOString(dates.startDate.date) : null;
			const dueDate = dates?.dueDate?.date != null ? toISOString(dates.dueDate.date) : null;

			const bulkStartDateUpdate = dates?.startDate
				? {
						date: startDate,
					}
				: null;

			const bulkDueDateUpdate = dates?.dueDate
				? {
						date: dueDate,
					}
				: null;

			const issueStartDate = issue.startDate != null ? toISOString(issue.startDate) : null;
			const issueDueDate = issue.dueDate != null ? toISOString(issue.dueDate) : null;

			// temporarily lock the issue to prevent updates coming in from realtime updates
			lockIssue(Number(id));

			const updateItemsInCacheList: UpdateItemsInCacheList = [];

			updateItemsInCacheList.push({
				bulkUpdates: [
					{ id, fields: { startDate: bulkStartDateUpdate, dueDate: bulkDueDateUpdate } },
				],
				originalValues: [{ id, fields: { startDate: issueStartDate, dueDate: issueDueDate } }],
			});

			const shouldScheduleChildren = shouldScheduleChildrenIssues({
				issue,
				modifiedDates: dates,
			});

			if (shouldScheduleChildren) {
				const populateChildUpdates = (parentIssueId: string) => {
					const childrenOnCache = childrenQueryData[parentIssueId]?.data?.issues;
					if (childrenOnCache) {
						const { bulkUpdates, originalValues } = getIssuesBulkDatesUpdate({
							issue,
							issueChildren: childrenOnCache,
							dates,
							timelineDuration,
						});

						updateItemsInCacheList.push({ bulkUpdates, originalValues });
						childrenOnCache.forEach((child) => populateChildUpdates(child.issueId));
					}
				};

				populateChildUpdates(id);
			}

			updateItemsInCacheList.forEach(({ bulkUpdates }) => {
				if (bulkUpdates.length) {
					updateItemsInCache(bulkUpdates);
				}
			});

			const rollbackUpdates = () => {
				updateItemsInCacheList.forEach(({ originalValues }) => {
					if (originalValues.length) {
						updateItemsInCache(originalValues);
					}
				});
			};

			// There are four kind of operations when the user set the start and due date on the same time
			// 1. User can SET new due and start date just clicking on the timeline
			// 2. User can DRAG along and update both dates
			// 3. User can update new due and start date on the Edit Dates modal
			// 4. User can remove dates on meatballs menu
			// For now we can detect the operation DRAG if the user has defined previously start or due date
			// once we implement rollup dates, the user can drag just without any dates.
			// TODO: JET-3804 revisit condition once rollup dates is implemented.
			const isDragOperation =
				startDate != null && dueDate != null && (issueStartDate != null || issueDueDate != null);

			startExperience();

			// TODO: HRZ-917 replace the following once the backend api that updates the descendant items is implemented.
			// The following is a temporary workaround to update item and descendant issues
			let isParentItemSuccessfullyUpdated = false;

			const onComplete = () => {
				markExperienceSuccess();

				fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem success', {
					issueId: id,
					startDate,
					dueDate,
				});

				if (!isParentItemSuccessfullyUpdated) {
					showFlag(SCHEDULE_ITEM_FAIL_FLAG);
					rollbackUpdates();
				}
			};

			const onError = (err: Error | ValidationError | FetchError | ApolloError | AbortError) => {
				if (err instanceof AbortError) {
					markExperienceAborted(err.message || 'scheduleItem aborted');
					fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem aborted', {
						error: err,
						startDate,
						dueDate,
					});

					return;
				}

				if ('statusCode' in err && err.statusCode === 404) {
					showFlag(
						fg('jira-issue-terminology-refresh-m3')
							? SCHEDULE_ITEM_NOT_FOUND_ISSUE_TERM_REFRESH
							: SCHEDULE_ITEM_NOT_FOUND,
					);
					return;
				}

				// Exclude client fetch, validation and 4xx errors from experience fail events reporting
				if (isClientFetchError(err) || isValidationError(err) || isHttpClientErrorResponse(err)) {
					markExperienceSuccess();
				} else {
					markExperienceFailed('schedule item failed', err);
				}

				fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem failed', {
					error: err,
					startDate,
					dueDate,
				});

				showFlag(SCHEDULE_ITEM_FAIL_FLAG);
				rollbackUpdates();
			};

			const finalise = () => {
				if (startDate != null && !startDateField) {
					showFlag(START_DATE_MISSING_FLAG);
				}
			};

			onScheduleItem({
				issueId: id,
				isDragOperation,
				...dates,
			})
				.then((success) => {
					if (success === true) {
						isParentItemSuccessfullyUpdated = true;
					}
					onComplete();
				})
				.catch(onError)
				.finally(finalise);
		},
		[
			issuesMap,
			lockIssue,
			createAnalyticsEvent,
			startExperience,
			onScheduleItem,
			childrenQueryData,
			timelineDuration,
			updateItemsInCache,
			markExperienceSuccess,
			showFlag,
			markExperienceAborted,
			markExperienceFailed,
			startDateField,
		],
	);

	return { onItemUpdate };
};
